import {createAsyncThunk} from '@reduxjs/toolkit';
import {getJWTToken} from '../../lib/auth-token';
import {handleAuthFailure, signOutExpiredSession} from '../../lib/auth-error-handler';
import {getWorkerApiUrl, apiFetch} from '../../lib/api';
import {ApiError, toApiError, toNetworkError} from '../../lib/apiError';
import type {
  AdminConversionListResponse,
  AdminEmailListResponse,
  AdminRecipientSuppressedError,
  AdminResendSetupEmailResponse,
  AdminSignupListResponse,
  AdminStats,
  AdminSuppressionListResponse,
  AdminTranscriptionListResponse,
  AdminUserDetailResponse,
  AdminUserListResponse,
} from '../../backend-types/admin-api';
import type {
  AdminConversion,
  AdminDashboardStats,
  AdminEmail,
  AdminEmailQuery,
  AdminPage,
  AdminSignup,
  AdminSignupQuery,
  AdminSuppression,
  AdminSuppressionQuery,
  AdminTranscription,
  AdminTranscriptionQueueQuery,
  AdminUser,
  AdminUserProfile,
  AdminUserQuery,
} from '../../domain/admin';
import {
  toAdminConversionPage,
  toAdminEmailPage,
  toAdminSignupPage,
  toAdminStats,
  toAdminSuppression,
  toAdminSuppressionPage,
  toAdminTranscriptionPage,
  toAdminUserPage,
  toAdminUserProfile,
} from '../../domain/adminMappers';

/**
 * Every read in the admin panel, plus its one write.
 *
 * ## 404 is "you are not an admin"
 *
 * The admin routes answer a non-admin with 404 rather than 403, on purpose: `/rest/v1/admin/*` does
 * not confirm it exists to someone who is not allowed in. So a 404 here can never be treated as
 * "that user id is wrong" — it is an authorization answer, and it is the *only* authorization
 * answer that counts. The `censor_me_is_admin` JWT claim decides whether the nav is drawn and
 * nothing else: it is stamped at token issue and keeps saying true after a revoke.
 *
 * `NOT_ADMIN_CODE` is stamped on the rejection so the slice can flip one flag and let `AdminRoute`
 * bounce the whole section, instead of every screen having to recognise a 404 for itself.
 *
 * ## Billing is read-only
 *
 * There is deliberately no comp / extend / cancel thunk in this file and there should never be one:
 * `reconcileStalePaddleSubscriptions()` re-syncs from Paddle on a cron tick, so a manual edit would
 * be reverted within minutes. The panel links out to Paddle instead.
 */

/** Stamped on any admin rejection that came back 404. See above. */
export const NOT_ADMIN_CODE = 'not_admin';

/** The resend endpoint's 409. See {@link resendSetupEmail}. */
export const RECIPIENT_SUPPRESSED_CODE = 'recipient_suppressed';

/**
 * A 409 from the resend endpoint, carrying the suppression itself and the remedy text. Kept as
 * structured data rather than flattened into a sentence, because the UI has to offer the override
 * and name the address that is blocked.
 */
export interface RecipientSuppressedError extends ApiError {
  code: typeof RECIPIENT_SUPPRESSED_CODE;
  suppression: AdminSuppression;
  /** Human-readable next step from the server, safe to show verbatim. */
  remedy: string;
}

export const isRecipientSuppressedError = (value: unknown): value is RecipientSuppressedError =>
  typeof value === 'object'
  && value !== null
  && (value as RecipientSuppressedError).code === RECIPIENT_SUPPRESSED_CODE;

interface AdminRequest {
  path: string;
  method?: string;
  body?: unknown;
  fallbackMessage: string;
}

/**
 * One place that attaches the token, normalises the failure and recognises the 404. Written as a
 * plain function taking `dispatch` rather than as a thunk so the sign-out on a 401 behaves the same
 * as everywhere else in the app.
 */
async function adminRequest<TWire>(
  {path, method = 'GET', body, fallbackMessage}: AdminRequest,
  dispatch: (action: any) => unknown
): Promise<{ok: true; data: TWire} | {ok: false; error: ApiError; bodyText: string; status: number}> {
  try {
    const jwt = await getJWTToken();

    if (!jwt) {
      return {
        ok: false,
        status: 401,
        bodyText: '',
        error: {status: 401, message: 'No authentication token available'},
      };
    }

    const headers: Record<string, string> = {Authorization: `Bearer ${jwt}`};
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await apiFetch(`${getWorkerApiUrl()}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();

      if (response.status === 401) {
        signOutExpiredSession(dispatch);
      }

      if (response.status === 404) {
        // Not "no such route" and not "no such user" — the admin API hides itself from non-admins.
        return {
          ok: false,
          status: 404,
          bodyText: errorText,
          error: {
            status: 404,
            message: 'This account is not an administrator.',
            code: NOT_ADMIN_CODE,
          },
        };
      }

      return {
        ok: false,
        status: response.status,
        bodyText: errorText,
        error: toApiError(response.status, errorText, fallbackMessage),
      };
    }

    return {ok: true, data: (await response.json()) as TWire};
  } catch (error) {
    handleAuthFailure(error, dispatch);

    return {
      ok: false,
      status: 0,
      bodyText: '',
      error: toNetworkError(error, fallbackMessage),
    };
  }
}

const buildQuery = (params: Record<string, string | number | boolean | undefined>): string => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });

  const query = search.toString();
  return query ? `?${query}` : '';
};

/* ==========================================================================
 * Dashboard
 * ========================================================================== */

export const fetchAdminStats = createAsyncThunk<
  AdminDashboardStats,
  void,
  {rejectValue: ApiError}
>('admin/fetchStats', async (_, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminStats>(
    {path: '/rest/v1/admin/stats', fallbackMessage: 'Could not load the admin dashboard'},
    dispatch
  );

  return result.ok ? toAdminStats(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * User list
 * ========================================================================== */

export const fetchAdminUsers = createAsyncThunk<
  AdminPage<AdminUser>,
  AdminUserQuery,
  {rejectValue: ApiError}
>('admin/fetchUsers', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminUserListResponse>(
    {
      path: `/rest/v1/admin/users${buildQuery({
        search: query.search,
        status: query.status,
        plan: query.plan,
        // These two filters have one meaningful value each — `password_set=false` finds accounts
        // that never chose one, `in_flight=true` finds users with a job running. Sending the other
        // half of the boolean would be a different (and unsupported) question.
        password_set: query.passwordSetFalse ? 'false' : undefined,
        in_flight: query.inFlight ? 'true' : undefined,
        is_admin: query.isAdmin === undefined ? undefined : String(query.isAdmin),
        created_after: query.createdAfter,
        created_before: query.createdBefore,
        sort: query.sort,
        order: query.order,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load users',
    },
    dispatch
  );

  return result.ok ? toAdminUserPage(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * User detail
 * ========================================================================== */

export const fetchAdminUserDetail = createAsyncThunk<
  AdminUserProfile,
  string,
  {rejectValue: ApiError}
>('admin/fetchUserDetail', async (userId, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminUserDetailResponse>(
    {
      path: `/rest/v1/admin/users/${encodeURIComponent(userId)}`,
      fallbackMessage: 'Could not load this user',
    },
    dispatch
  );

  return result.ok ? toAdminUserProfile(result.data) : rejectWithValue(result.error);
});

interface AdminActivityQuery {
  userId: string;
  limit?: number;
  offset?: number;
}

export const fetchAdminUserTranscriptions = createAsyncThunk<
  AdminPage<AdminTranscription>,
  AdminActivityQuery,
  {rejectValue: ApiError}
>('admin/fetchUserTranscriptions', async ({userId, limit, offset}, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminTranscriptionListResponse>(
    {
      path: `/rest/v1/admin/users/${encodeURIComponent(userId)}/transcriptions${buildQuery({limit, offset})}`,
      fallbackMessage: 'Could not load transcriptions',
    },
    dispatch
  );

  return result.ok ? toAdminTranscriptionPage(result.data) : rejectWithValue(result.error);
});

export const fetchAdminUserConversions = createAsyncThunk<
  AdminPage<AdminConversion>,
  AdminActivityQuery,
  {rejectValue: ApiError}
>('admin/fetchUserConversions', async ({userId, limit, offset}, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminConversionListResponse>(
    {
      path: `/rest/v1/admin/users/${encodeURIComponent(userId)}/conversions${buildQuery({limit, offset})}`,
      fallbackMessage: 'Could not load conversions',
    },
    dispatch
  );

  return result.ok ? toAdminConversionPage(result.data) : rejectWithValue(result.error);
});

export const fetchAdminUserEmails = createAsyncThunk<
  AdminPage<AdminEmail>,
  AdminActivityQuery,
  {rejectValue: ApiError}
>('admin/fetchUserEmails', async ({userId, limit, offset}, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminEmailListResponse>(
    {
      path: `/rest/v1/admin/users/${encodeURIComponent(userId)}/emails${buildQuery({limit, offset})}`,
      fallbackMessage: 'Could not load the email history',
    },
    dispatch
  );

  return result.ok ? toAdminEmailPage(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Global email log and suppressions
 * ========================================================================== */

export const fetchAdminEmails = createAsyncThunk<
  AdminPage<AdminEmail>,
  AdminEmailQuery,
  {rejectValue: ApiError}
>('admin/fetchEmails', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminEmailListResponse>(
    {
      path: `/rest/v1/admin/emails${buildQuery({
        status: query.status,
        email_type: query.emailType,
        since: query.since,
        search: query.search,
        order: query.order,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load the email log',
    },
    dispatch
  );

  return result.ok ? toAdminEmailPage(result.data) : rejectWithValue(result.error);
});

export const fetchAdminSuppressions = createAsyncThunk<
  AdminPage<AdminSuppression>,
  AdminSuppressionQuery,
  {rejectValue: ApiError}
>('admin/fetchSuppressions', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminSuppressionListResponse>(
    {
      path: `/rest/v1/admin/suppressions${buildQuery({
        include_removed: query.includeRemoved ? 'true' : undefined,
        search: query.search,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load the suppression list',
    },
    dispatch
  );

  return result.ok ? toAdminSuppressionPage(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * Global transcription queue
 * ========================================================================== */

export const fetchAdminTranscriptionQueue = createAsyncThunk<
  AdminPage<AdminTranscription>,
  AdminTranscriptionQueueQuery,
  {rejectValue: ApiError}
>('admin/fetchTranscriptionQueue', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminTranscriptionListResponse>(
    {
      path: `/rest/v1/admin/transcriptions${buildQuery({
        status: query.status,
        since: query.since,
        order: query.order,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load the transcription queue',
    },
    dispatch
  );

  return result.ok ? toAdminTranscriptionPage(result.data) : rejectWithValue(result.error);
});

/* ==========================================================================
 * The one write
 * ========================================================================== */

export interface ResendSetupEmailResult {
  recipient: string;
  emailLogId: string | null;
  /** True when this went out over a known suppression — warn, do not confirm success. */
  forcedPastSuppression: boolean;
}

/**
 * Sends a fresh magic link — the same email a new subscriber gets after checkout, and for a
 * Paddle-provisioned account the only way in, since they have no password.
 *
 * **409 `recipient_suppressed` is the interesting failure.** Resend suppresses an address after a
 * hard bounce and then keeps *accepting* sends to it: it returns a message id and delivers nothing.
 * The endpoint refuses rather than letting an operator click resend into a black hole, and the fix
 * lives in the Resend dashboard, which is what `remedy` says. `force: true` sends anyway — the
 * response then carries `forced_past_suppression`, which almost certainly means it was dropped.
 *
 * Success and failure are both written to the user's email timeline, so the caller refetches the
 * detail on either outcome rather than only on the happy path.
 */
export const resendSetupEmail = createAsyncThunk<
  ResendSetupEmailResult,
  {userId: string; force?: boolean},
  {rejectValue: ApiError | RecipientSuppressedError}
>('admin/resendSetupEmail', async ({userId, force}, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminResendSetupEmailResponse>(
    {
      path: `/rest/v1/admin/users/${encodeURIComponent(userId)}/resend-setup-email${
        force ? '?force=true' : ''
      }`,
      method: 'POST',
      fallbackMessage: 'The setup email could not be sent',
    },
    dispatch
  );

  if (!result.ok) {
    if (result.status === 409) {
      try {
        const body = JSON.parse(result.bodyText) as AdminRecipientSuppressedError;

        if (body.code === RECIPIENT_SUPPRESSED_CODE && body.suppression) {
          return rejectWithValue({
            status: 409,
            message: body.error || 'That address is suppressed.',
            code: RECIPIENT_SUPPRESSED_CODE,
            suppression: toAdminSuppression(body.suppression),
            remedy: body.remedy,
          } satisfies RecipientSuppressedError);
        }
      } catch {
        // Fall through to the generic error — a 409 we cannot parse is still a 409.
      }
    }

    return rejectWithValue(result.error);
  }

  return {
    recipient: result.data.recipient,
    emailLogId: result.data.email_log_id ?? null,
    forcedPastSuppression: result.data.forced_past_suppression === true,
  };
});

/* ==========================================================================
 * Signup queue
 * ========================================================================== */

export const fetchAdminSignups = createAsyncThunk<
  AdminPage<AdminSignup>,
  AdminSignupQuery,
  {rejectValue: ApiError}
>('admin/fetchSignups', async (query, {dispatch, rejectWithValue}) => {
  const result = await adminRequest<AdminSignupListResponse>(
    {
      path: `/rest/v1/admin/signups${buildQuery({
        status: query.status,
        search: query.search,
        limit: query.limit,
        offset: query.offset,
      })}`,
      fallbackMessage: 'Could not load the signup queue',
    },
    dispatch
  );

  return result.ok ? toAdminSignupPage(result.data) : rejectWithValue(result.error);
});
